/**
 * Global Upgrade Prompt — opens whenever any axios call returns HTTP 402
 * (plan_quota.assert_quota raised). Renders a side-by-side comparison of the
 * tenant's current plan and the suggested next plan, with primary CTAs to
 * upgrade or open the full pricing page.
 *
 * Mount once at app root via <UpgradePromptProvider>. Listens for:
 *   1. axios 402 responses (auto-open with backend's structured payload)
 *   2. `lawos:upgrade-prompt` window event with {resource} (manual open)
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { X, ArrowRight, Sparkles, Check, Crown, Lock, Clock4, ExternalLink, Zap, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const UpgradePromptCtx = createContext(null);

export function useUpgradePrompt() {
    const ctx = useContext(UpgradePromptCtx);
    if (!ctx) throw new Error("useUpgradePrompt must be used inside <UpgradePromptProvider>");
    return ctx;
}

export function UpgradePromptProvider({ children }) {
    const [payload, setPayload] = useState(null);   // null = closed, object = open

    const open = useCallback((p) => setPayload(p), []);
    const close = useCallback(() => setPayload(null), []);

    // Manual open via custom window event — lets any component trigger the modal
    // without having to import the hook (handy from utility files).
    useEffect(() => {
        const handler = async (ev) => {
            const resource = ev?.detail?.resource;
            if (!resource) return;
            try {
                const { data } = await api.get(`/plan/upgrade-preview/${resource}`);
                setPayload(data);
            } catch (e) {
                // silent — modal stays closed if the preview endpoint fails
                // (e.g. logged-out, unknown resource)
            }
        };
        window.addEventListener("lawos:upgrade-prompt", handler);
        return () => window.removeEventListener("lawos:upgrade-prompt", handler);
    }, []);

    // Axios response interceptor — auto-open on any 402 from the backend.
    useEffect(() => {
        const id = api.interceptors.response.use(
            (resp) => resp,
            (err) => {
                if (err?.response?.status === 402) {
                    const d = err.response.data?.detail;
                    if (d?.error === "quota_exceeded") {
                        setPayload(d);
                    }
                }
                return Promise.reject(err);
            },
        );
        return () => api.interceptors.response.eject(id);
    }, []);

    const value = useMemo(() => ({ open, close }), [open, close]);

    return (
        <UpgradePromptCtx.Provider value={value}>
            {children}
            {payload && <UpgradePromptModal payload={payload} onClose={close} />}
        </UpgradePromptCtx.Provider>
    );
}


// ──────────────────────────────── Modal ────────────────────────────────

function UpgradePromptModal({ payload, onClose }) {
    const current = payload.current_plan || {};
    const suggested = payload.suggested_plan || {};
    const hasSuggested = !!suggested.code;
    const resetsAt = payload.resets_at ? new Date(payload.resets_at) : null;

    // Close on Esc
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
             data-testid="upgrade-prompt-modal" onClick={onClose}>
            <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="relative px-6 py-5 border-b border-border bg-gradient-to-br from-primary/5 to-transparent">
                    <button onClick={onClose} data-testid="upgrade-prompt-close"
                        className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition">
                        <X className="w-4 h-4"/>
                    </button>
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-xl bg-primary/10 text-primary">
                            <Lock className="w-5 h-5"/>
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary mb-1">
                                {payload.resource_label || "Quota"} limit reached
                            </div>
                            <h2 className="font-heading text-xl font-bold text-foreground">
                                You've used {payload.used} of {payload.cap} {payload.resource_unit} this {payload.period}.
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">{payload.message}</p>
                            {resetsAt && (
                                <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1.5">
                                    <Clock4 className="w-3 h-3"/>
                                    Resets on {resetsAt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Top-up packs (transactional) — shown only when the
                    backend included at least one pack for this resource.
                    The firm gets the option to keep going *this month*
                    without committing to a higher tier. */}
                {Array.isArray(payload.topup_packs) && payload.topup_packs.length > 0 && (
                    <TopupPackSection
                        packs={payload.topup_packs}
                        resource={payload.resource}
                        resourceLabel={payload.resource_label}
                        onPaid={onClose}
                    />
                )}

                {/* Side-by-side comparison */}
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PlanCard plan={current}  variant="current"  resource={payload.resource}/>
                    {hasSuggested
                        ? <PlanCard plan={suggested} variant="suggested" resource={payload.resource}/>
                        : <EnterpriseCard/>}
                </div>

                {/* Footer CTAs */}
                <div className="px-6 py-4 border-t border-border flex flex-wrap items-center justify-between gap-3 bg-muted/30">
                    <Link to="/pricing" onClick={onClose} data-testid="upgrade-prompt-see-all"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition">
                        See all plans <ExternalLink className="w-3 h-3"/>
                    </Link>
                    {hasSuggested && (
                        <Link to={`/app/billing/plans?upgrade=${suggested.code}`}
                            onClick={onClose}
                            data-testid="upgrade-prompt-cta"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow-sm hover:opacity-95 transition">
                            <Sparkles className="w-4 h-4"/>
                            Upgrade to {suggested.name} · ₹{Math.round(suggested.price_monthly_inr)}/mo
                            <ArrowRight className="w-4 h-4"/>
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}


// ──────────────────────────────── Plan card ────────────────────────────────

function PlanCard({ plan, variant, resource }) {
    const isCurrent = variant === "current";
    const cap = plan?.quotas?.[resource];
    const capDisplay = (cap === null || cap === undefined) ? "Unlimited" : cap.toLocaleString();
    const accent = isCurrent
        ? "border-border bg-background"
        : "border-primary bg-primary/5 ring-1 ring-primary/20";
    const Icon  = isCurrent ? Lock : Crown;

    const monthly = Number(plan?.price_monthly_inr || 0);
    const annual  = Number(plan?.price_annual_inr || 0);
    const showAnnualSave = monthly > 0 && annual > 0 && annual < monthly * 12;

    return (
        <div className={`rounded-2xl border p-4 ${accent}`} data-testid={`upgrade-card-${variant}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground inline-flex items-center gap-1.5">
                    <Icon className="w-3 h-3"/>
                    {isCurrent ? "Your plan" : "Recommended"}
                </div>
                {!isCurrent && (
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                        Upgrade
                    </span>
                )}
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground">{plan?.name || "—"}</h3>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">{plan?.description || ""}</p>

            <div className="mt-3 flex items-baseline gap-1">
                <span className="font-heading text-2xl font-bold text-foreground">
                    ₹{Math.round(monthly).toLocaleString()}
                </span>
                <span className="text-xs text-muted-foreground">/ month</span>
            </div>
            {showAnnualSave && (
                <div className="text-[11px] text-emerald-700 dark:text-emerald-400 font-medium">
                    ₹{Math.round(annual).toLocaleString()}/yr · Save ₹{Math.round(monthly * 12 - annual).toLocaleString()}
                </div>
            )}

            {/* Highlighted quota for the resource that triggered the modal */}
            <div className={`mt-4 rounded-xl px-3 py-2.5 ${isCurrent ? "bg-muted" : "bg-primary/10"}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-0.5">
                    {RESOURCE_LABEL[resource] || resource}
                </div>
                <div className="font-mono font-bold text-foreground">
                    {capDisplay} {capDisplay !== "Unlimited" && (RESOURCE_UNIT[resource] || "")}
                </div>
            </div>

            {/* Other quotas (just for the suggested plan) */}
            {!isCurrent && plan?.quotas && (
                <div className="mt-3 space-y-1.5">
                    {QUOTA_ORDER.filter(k => k !== resource).map(k => {
                        const v = plan.quotas[k];
                        const label = RESOURCE_LABEL[k];
                        if (!label) return null;
                        return (
                            <div key={k} className="flex items-center gap-1.5 text-xs text-foreground/80">
                                <Check className="w-3 h-3 text-emerald-600 shrink-0"/>
                                <span className="truncate">
                                    {v === null ? "Unlimited" : v.toLocaleString()} {RESOURCE_UNIT[k]} · {label}
                                </span>
                            </div>
                        );
                    })}
                    <div className="flex items-center gap-1.5 text-xs text-foreground/80">
                        <Check className="w-3 h-3 text-emerald-600 shrink-0"/>
                        <span>{plan.seats_default || 1} seat{(plan.seats_default || 1) > 1 ? "s" : ""} included · {plan.storage_gb_included || 0} GB storage</span>
                    </div>
                </div>
            )}
        </div>
    );
}

function EnterpriseCard() {
    return (
        <div className="rounded-2xl border border-primary bg-primary/5 ring-1 ring-primary/20 p-4" data-testid="upgrade-card-enterprise">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary mb-1 inline-flex items-center gap-1.5">
                <Crown className="w-3 h-3"/> Enterprise
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground">Talk to Sales</h3>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                You're on our top public tier. Reach out for a custom plan with unlimited everything, multi-branch
                hierarchy, and 99.95% SLA.
            </p>
            <a href="mailto:sales@lawos.in?subject=Enterprise%20plan%20enquiry"
               className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:opacity-95">
                Contact sales <ArrowRight className="w-3 h-3"/>
            </a>
        </div>
    );
}

// Keep these in sync with backend `plan_quota.RESOURCE_META`
const QUOTA_ORDER = [
    "cnr_sync_per_month",
    "ai_credits_per_month",
    "whatsapp_msgs_per_month",
    "cases_total",
    "clients_total",
    "documents_total",
];
const RESOURCE_LABEL = {
    cnr_sync_per_month:      "eCourts CNR lookups / month",
    ai_credits_per_month:    "AI credits / month",
    whatsapp_msgs_per_month: "WhatsApp messages / month",
    cases_total:             "Total cases",
    clients_total:           "Total clients",
    documents_total:         "Total documents",
};
const RESOURCE_UNIT = {
    cnr_sync_per_month:      "lookups",
    ai_credits_per_month:    "credits",
    whatsapp_msgs_per_month: "msgs",
    cases_total:             "cases",
    clients_total:           "clients",
    documents_total:         "docs",
};


// ──────────────────────────── Top-up packs ────────────────────────────

/**
 * Renders the *Buy add-on pack* funnel inside the modal. One-time purchases
 * that top up the current month's quota for the resource that triggered
 * the modal. Click → backend creates a Razorpay order → Razorpay checkout
 * opens → on `payment.captured` the webhook credits units to the current
 * `period`. We close the modal optimistically the moment the checkout
 * succeeds and reload the page so the next API call sees the boosted cap.
 */
function TopupPackSection({ packs, resource, resourceLabel, onPaid }) {
    const [busyCode, setBusyCode] = useState(null);

    const ensureRazorpay = () => new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve();
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = resolve; s.onerror = () => reject(new Error("Razorpay SDK failed to load"));
        document.body.appendChild(s);
    });

    const buy = async (pack) => {
        setBusyCode(pack.code);
        try {
            await ensureRazorpay();
            const { data } = await api.post("/billing/topups/order", { pack_code: pack.code });
            const rzp = new window.Razorpay({
                key: data.key_id,
                order_id: data.order_id,
                amount: data.amount_paise,
                currency: data.currency || "INR",
                name: "Law OS",
                description: data.pack_name,
                prefill: {
                    name: data.prefill_name || "",
                    email: data.prefill_email || "",
                    contact: data.prefill_contact || "",
                },
                handler: () => {
                    toast.success(`${data.pack_name} added — refreshing…`);
                    // Give the webhook a beat to land, then reload so the
                    // boosted cap is reflected in the next API call.
                    setTimeout(() => {
                        onPaid?.();
                        window.location.reload();
                    }, 1200);
                },
                modal: { ondismiss: () => setBusyCode(null) },
                theme: { color: "#2D3A8C" },
            });
            rzp.open();
        } catch (e) {
            toast.error(apiError(e) || "Could not start checkout");
            setBusyCode(null);
        }
    };

    return (
        <div className="px-6 pt-5 pb-4 border-b border-border" data-testid="topup-pack-section">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary mb-0.5 inline-flex items-center gap-1.5">
                        <Zap className="w-3 h-3"/> Need just this month?
                    </div>
                    <h3 className="font-heading text-sm font-bold text-foreground">
                        Top up your {resourceLabel?.toLowerCase() || "quota"} — no commitment
                    </h3>
                </div>
                <span className="text-[10px] text-muted-foreground hidden sm:inline">One-time charge · no auto-renew</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {packs.map((p) => {
                    const isBusy = busyCode === p.code;
                    return (
                        <button
                            key={p.code}
                            onClick={() => buy(p)}
                            disabled={!!busyCode}
                            data-testid={`topup-pack-buy-${p.code}`}
                            className="group text-left rounded-xl border border-border hover:border-primary bg-background hover:bg-primary/5 transition px-4 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <div className="flex items-baseline justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-semibold text-sm text-foreground truncate">{p.name}</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{p.description}</div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-mono text-base font-bold text-foreground">₹{Math.round(p.price_inr).toLocaleString()}</div>
                                    <div className="text-[10px] text-muted-foreground">
                                        ₹{(p.price_inr / Math.max(1, p.units)).toFixed(2)} / {RESOURCE_UNIT[resource] || "unit"}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary group-hover:gap-2 transition-all">
                                {isBusy ? (
                                    <><Loader2 className="w-3 h-3 animate-spin"/> Starting checkout…</>
                                ) : (
                                    <>Buy now <ArrowRight className="w-3 h-3"/></>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
